import {ColumnsType} from 'antd/es/table';
import {formatString, formatCurrency, discreteFilterIcon, getSearchFilterProps} from '../../../utils/helpers';
import React from 'react';
import {Image, Tag, Tooltip} from 'antd';

const renderStatusTag = (status?: string) => {
    if (!status) return <Tag>Unknown</Tag>;

    const color = status === 'completed' || status === 'paid'
        ? 'green'
        : status === 'failed'
            ? 'red'
            : 'gold';

    return <Tag color={color}>{formatString(status)}</Tag>;
};

const renderLabeledStatusTag = (label: string, status?: string) => {
    const color = status === 'completed' || status === 'paid'
        ? 'green'
        : status === 'failed'
            ? 'red'
            : 'gold';

    return <Tag color={color}>{label}</Tag>;
};

const renderTransactionStatus = (status?: string, record?: any) => {
    if (record?.transactionType !== 'cash_out') {
        return renderStatusTag(status);
    }

    const label = status === 'completed'
        ? 'Transfer completed'
        : status === 'failed'
            ? 'Transfer failed'
            : 'Transfer pending';

    const title = status === 'completed'
        ? 'Funds were moved to the connected payout account. Bank payout is tracked separately.'
        : 'This is the transfer status before bank payout processing.';

    return (
        <Tooltip title={title}>
            <span>{renderLabeledStatusTag(label, status)}</span>
        </Tooltip>
    );
};

const renderPayoutStatus = (payoutStatus?: string) => {
    const status = payoutStatus || 'pending';
    const label = status === 'paid'
        ? 'Bank paid'
        : status === 'failed'
            ? 'Bank payout failed'
            : 'Bank processing';

    const title = status === 'paid'
        ? 'Stripe marked this payout as paid. Some banks may take a little longer to display the deposit.'
        : status === 'failed'
            ? 'Stripe could not complete the bank payout.'
            : 'Stripe has initiated the bank payout. Standard payouts usually arrive in 1-4 business days.';

    return (
        <Tooltip title={title}>
            <span>{renderLabeledStatusTag(label, status)}</span>
        </Tooltip>
    );
};


export const myCampaignsTableColumns: ColumnsType<any> = [
    {
        title: 'Logo',
        width: 80,
        dataIndex: ['logo', 'signedUrl'],
        key: 'logo',
        render: (url: string, record: any) => {
            if (record?.logo?.signedUrl) {
                return (
                    <Image
                        src={record.logo.signedUrl}
                        alt="Campaign Logo"
                        width={50}
                        height={50}
                        style={{objectFit: 'cover', borderRadius: 4}}
                        preview={false}
                    />
                );
            }
            return '-';
        }
    },
    {
        title: 'Title',
        width: 150,
        dataIndex: 'title',
        key: 'title',
        sorter: true,
        ...getSearchFilterProps('title', 'Search Title')
    },
    {
        title: 'Target URL',
        width: 100,
        dataIndex: 'targetURL',
        key: 'targetURL',
        render: (url: string) => (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a>
        )
    },
    {
        title: 'Click rate',
        width: 100,
        dataIndex: 'clickRate',
        key: 'clickRate',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Initial budget',
        width: 100,
        dataIndex: 'initialBudget',
        key: 'initialBudget',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Balance',
        width: 100,
        dataIndex: 'balance',
        key: 'balance',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Clicks',
        width: 100,
        dataIndex: 'clickCounter',
        key: 'clickCounter',
        sorter: true
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: formatString,
        filters: [
            {text: 'Active', value: 'active'},
            {text: 'Draft', value: 'draft'},
            {text: 'Pending cancellation', value: 'pending_cancellation'},
            {text: 'Cancelled', value: 'cancelled'}
        ],
        onFilter: (value, record) => record.status === value,
        sorter: true,
        filterIcon: discreteFilterIcon
    }
];

export const activeCampaignsTableColumns: ColumnsType<any> = [
    {
        title: 'Logo',
        width: 80,
        dataIndex: ['logo', 'signedUrl'],
        key: 'logo',
        render: (url: string, record: any) => {
            if (record?.logo?.signedUrl) {
                return (
                    <Image
                        src={record.logo.signedUrl}
                        alt="Campaign Logo"
                        width={50}
                        height={50}
                        style={{objectFit: 'cover', borderRadius: 4}}
                        preview={false}
                    />
                );
            }
            return '-';
        }
    },
    {
        title: 'Title',
        width: 150,
        dataIndex: 'title',
        key: 'title',
        sorter: true,
        ...getSearchFilterProps('title', 'Search Title')
    },
    {
        title: 'Advertiser',
        width: 100,
        dataIndex: ['advertiserId', 'username'],
        key: "advertiserId.username",
        sorter: true
    },
    {
        title: 'Target URL',
        width: 100,
        dataIndex: 'targetURL',
        key: 'targetURL',
        render: (url: string) => (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{url}</a>
        )
    },
    {
        title: 'Click rate',
        width: 100,
        dataIndex: 'clickRate',
        key: 'clickRate',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Initial budget',
        width: 100,
        dataIndex: 'initialBudget',
        key: 'initialBudget',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Balance',
        width: 100,
        dataIndex: 'balance',
        key: 'balance',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Clicks',
        width: 100,
        dataIndex: 'clickCounter',
        key: 'clickCounter',
        sorter: true
    }
];

export const myCampaignLinkTableColumns: ColumnsType<any> = [
    {
        title: 'Title',
        width: 150,
        dataIndex: ['campaignId', 'title'],
        key: 'campaignId.title',
        sorter: true,
        ...getSearchFilterProps('campaignId.title', 'Search Title')
    },
    {
        title: 'Affiliate link',
        width: 100,
        dataIndex: 'customUrl',
        key: 'customUrl',
        render: (url: string) => (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Link here</a>
        )
    },
    {
        title: 'Click rate',
        width: 100,
        dataIndex: ['campaignId', 'clickRate'],
        key: 'campaignId.clickRate',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Balance',
        width: 100,
        dataIndex: ['campaignId', 'balance'],
        key: 'campaignId.balance',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'My clicks',
        width: 100,
        dataIndex: 'clickCounter',
        key: 'clickCounter',
        sorter: true,
    },
    {
        title: 'All clicks',
        width: 100,
        dataIndex: ['campaignId', 'clickCounter'],
        key: 'campaignId.clickCounter',
        sorter: true,
    },
    {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        sorter: true,
        render: (isActive: boolean) => isActive ? 'Active' : 'Inactive',
        filters: [
            {text: 'Active', value: true},
            {text: 'Inactive', value: false}
        ],
        onFilter: (value, record) => record.isActive === value,
        filterIcon: discreteFilterIcon
    }
];

export const userCampaignLinkTableColumns: ColumnsType<any> = [
    {
        title: 'Title',
        width: 150,
        dataIndex: ['campaignId', 'title'],
        key: 'campaignId.title',
        sorter: true,
        ...getSearchFilterProps('campaignId.title', 'Search Title')
    },
    {
        title: 'User',
        width: 100,
        dataIndex: ['ownerId', 'username'],
        key: 'ownerId.username'
    },
    {
        title: 'Affiliate link',
        width: 100,
        dataIndex: 'customUrl',
        key: 'customUrl',
        render: (url: string) => (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Link here</a>
        )
    },
    {
        title: 'Click rate',
        width: 100,
        dataIndex: ['campaignId', 'clickRate'],
        key: 'campaignId.clickRate',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Balance',
        width: 100,
        dataIndex: ['campaignId', 'balance'],
        key: 'campaignId.balance',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Campaign link clicks',
        width: 100,
        dataIndex: 'clickCounter',
        key: 'clickCounter',
        sorter: true
    },
    {
        title: 'All clicks',
        width: 100,
        dataIndex: ['campaignId', 'clickCounter'],
        key: 'campaignId.clickCounter',
        sorter: true,
    },
    {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        sorter: true,
        render: (isActive: boolean) => isActive ? 'Active' : 'Inactive',
        filters: [
            {text: 'Active', value: true},
            {text: 'Inactive', value: false}
        ],
        onFilter: (value, record) => record.isActive === value,
        filterIcon: discreteFilterIcon
    }
];

export const transactionsTableColumns: ColumnsType<any> = [
    {
        title: 'From',
        dataIndex: ['from', 'username'],
        render: (from: string) => from === 'promotify_system' ? 'promotify' : from,
        ...getSearchFilterProps('from.username', 'Search From')
    },
    {
        title: 'To',
        dataIndex: ['to', 'username'],
        render: (to: string) => to === 'promotify_system' ? 'promotify' : to,
        ...getSearchFilterProps('from.username', 'Search From')
    },
    {
        title: 'Type',
        dataIndex: 'transactionType',
        render: formatString,
        filters: [
            {text: 'Wallet deposit', value: 'wallet_deposit'},
            {text: 'Cash out', value: 'cash_out'},
            {text: 'Subscription', value: 'subscription_payment'},
        ],
        onFilter: (value, record) => record.transactionType === value,
        sorter: true,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Amount',
        dataIndex: 'amount',
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Currency',
        dataIndex: 'currency',
    },
    {
        title: 'Transfer',
        dataIndex: 'status',
        render: (status: string, record: any) => renderTransactionStatus(status, record),
        filters: [
            {text: 'Pending', value: 'pending'},
            {text: 'Completed', value: 'completed'},
            {text: 'Failed', value: 'failed'},
        ],
        onFilter: (value, record) => record.status === value,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Bank payout',
        dataIndex: 'payoutStatus',
        render: (payoutStatus: string, record: any) =>
            record.transactionType === 'cash_out' ? renderPayoutStatus(payoutStatus) : '-',
        filters: [
            {text: 'Pending', value: 'pending'},
            {text: 'Paid', value: 'paid'},
            {text: 'Failed', value: 'failed'},
        ],
        onFilter: (value, record) => record.payoutStatus === value,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Payment method',
        dataIndex: 'paymentMethod',
        render: formatString,
    },
    {
        title: 'Created at',
        dataIndex: 'createdAt',
        sorter: true,
        render: iso => new Date(iso).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        }),
    }
];

export const bestAdvertisersTableColumns: ColumnsType<any> = [
    {
        title: 'Username',
        width: 80,
        dataIndex: 'username',
        sorter: true
    },
    {
        title: 'User type',
        width: 100,
        dataIndex: 'userType',
        sorter: true,
        render: formatString,
        filters: [
            {text: 'Individual', value: 'individual'},
            {text: 'Business', value: 'business'}
        ],
        onFilter: (value, record) => record.userType === value
    },
    {
        title: 'Name',
        dataIndex: 'displayName',
        width: 100,
        sorter: true,
        ...getSearchFilterProps('displayName', 'Search Name')
    },
    {
        title: 'Active campaigns',
        dataIndex: 'activeCampaigns',
        width: 100,
        sorter: true
    },
    {
        title: 'Total amount deposited',
        dataIndex: 'totalDeposited',
        width: 120,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Total amount spent',
        dataIndex: 'totalSpent',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Total gained clicks',
        dataIndex: 'totalClicks',
        width: 100,
        sorter: true
    },
    {
        title: 'Average click rate',
        dataIndex: 'averageClickRate',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    }
];

export const bestPromotersTableColumns: ColumnsType<any> = [
    {
        title: 'Username',
        width: 100,
        dataIndex: 'username',
        sorter: true
    },
    {
        title: 'User type',
        width: 100,
        dataIndex: 'userType',
        sorter: true,
        render: formatString,
        filters: [
            {text: 'Individual', value: 'individual'},
            {text: 'Business', value: 'business'}
        ],
        onFilter: (value, record) => record.userType === value,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Name',
        dataIndex: 'displayName',
        width: 100,
        sorter: true,
        ...getSearchFilterProps('displayName', 'Search Name')
    },
    {
        title: 'Active campaign links',
        dataIndex: 'activeCampaignLinks',
        width: 100,
        sorter: true
    },
    {
        title: 'Total generated clicks',
        dataIndex: 'totalClicks',
        width: 100,
        sorter: true
    },
    {
        title: 'Earnings',
        dataIndex: 'totalMoneyGenerated',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Average click rate',
        dataIndex: 'averageClickRate',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    }
];

export const bestPartnersTableColumns: ColumnsType<any> = [
    {
        title: 'Username',
        width: 70,
        dataIndex: 'username',
        sorter: true
    },
    {
        title: 'User type',
        width: 90,
        dataIndex: 'userType',
        sorter: true,
        render: formatString,
        filters: [
            {text: 'Individual', value: 'individual'},
            {text: 'Business', value: 'business'}
        ],
        onFilter: (value, record) => record.userType === value,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Name',
        dataIndex: 'displayName',
        width: 70,
        sorter: true,
        ...getSearchFilterProps('displayName', 'Search Name')
    },
    {
        title: 'Onboarded users',
        dataIndex: 'onboardedUserCount',
        width: 120,
        sorter: true
    },
    {
        title: 'Onboarding earnings',
        dataIndex: 'onboardedUserRevenue',
        width: 130,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Active campaign links',
        dataIndex: 'activeCampaignLinks',
        width: 130,
        sorter: true
    },
    {
        title: 'Total clicks',
        dataIndex: 'totalClicks',
        width: 90,
        sorter: true
    },
    {
        title: 'Click earnings',
        dataIndex: 'totalMoneyGenerated',
        width: 90,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    },
    {
        title: 'Avg. click rate',
        dataIndex: 'averageClickRate',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    }
];

export const campaignPromotersTableColumns: ColumnsType<any> = [
    {
        title: 'Username',
        width: 100,
        dataIndex: 'username',
        sorter: true
    },
    {
        title: 'User type',
        width: 100,
        dataIndex: 'userType',
        sorter: true,
        render: formatString,
        filters: [
            {text: 'Individual', value: 'individual'},
            {text: 'Business', value: 'business'}
        ],
        onFilter: (value, record) => record.userType === value,
        filterIcon: discreteFilterIcon
    },
    {
        title: 'Name',
        dataIndex: 'displayName',
        width: 100,
        sorter: true,
        ...getSearchFilterProps('displayName', 'Search Name')
    },
    {
        title: 'Total clicks',
        dataIndex: 'totalClicks',
        width: 100,
        sorter: true
    },
    {
        title: 'Click rate',
        dataIndex: 'clickRate',
        width: 100,
        sorter: true,
        render: (v: number) => formatCurrency(v)
    },
    {
        title: 'Earnings',
        dataIndex: 'earnings',
        width: 100,
        sorter: true,
        render: (amount: number) => formatCurrency(amount)
    }
];
